import React, { useContext, useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { withRouter } from 'react-router'

import { fetchTourV2Offer } from 'actions/TourV2Actions'
import Error404Page from 'components/Pages/Error404Page'
import Error500Page from 'components/Pages/Error500Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import findCheapestTourV2PurchasableOption from 'lib/TourV2/findCheapestTourV2PurchasableOption'
import GeoContext from 'contexts/geoContext'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { tourOfferClicked } from 'analytics/eventDefinitions'
import useQueryParams from 'hooks/useQueryParams'
import { track } from 'analytics/analytics'
import { viewOfferEvent } from 'analytics/optimizely/events'

interface Props extends RouteComponentProps<{ offerId: string }> {
  fetching: boolean
  offerError?: any
  tourV2Offer: Tours.V2Offer
  currency: string
}

function TourV2OfferPage(props: Props) {
  const {
    fetching,
    match,
    offerError,
    tourV2Offer,
    currency,
  } = props

  const geo = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const privateRequestKey = useQueryParams().get('privateRequestKey')

  useEffect(() => {
    dispatch(fetchTourV2Offer(match.params.offerId, privateRequestKey))
  }, [dispatch, match.params.offerId, privateRequestKey])

  useEffect(() => {
    if (tourV2Offer) {
      fireEcommerceGTMEvent(tourOfferClicked(tourV2Offer, currency))
      track('view-offer', viewOfferEvent())
    }
  }, [currency, tourV2Offer])

  if (offerError) {
    if (offerError.status == 404) {
      return <Error404Page />
    } else {
      return <Error500Page />
    }
  }

  if (fetching || !tourV2Offer) {
    return <LoadingIndicator />
  }

  if (tourV2Offer) {
    const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(tourV2Offer)

    const variationId = cheapestPurchasableOption?.fkVariationId ?? Object.keys(tourV2Offer.variations)[0]
    const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${tourV2Offer.id}/${variationId}`
    return <RedirectWithStatus code={301} to={toUrl}/>
  }

  return null
}

const mapStateToProps = (appState: App.State, ownProps: Partial<Props>) => {
  const { offerId } = ownProps.match.params
  const query = ownProps.location.search

  const privateRequestKey = new URLSearchParams(query).get('privateRequestKey')
  const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

  const tourV2Offer = appState.offer.tourV2Offers[offerKey]

  return {
    fetching: appState.offer.offersLoading[offerKey],
    offerError: appState.offer.offerErrors[offerKey],
    currency: appState.geo.currentCurrency,
    tourV2Offer,
  }
}

export default withRouter(connect(mapStateToProps)(TourV2OfferPage))
